import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Autocomplete, Box, Button, IconButton, Stack, TextField } from "@mui/material";
import React, { useCallback, useEffect } from "react";
import { ResponseConfigOption } from "../../types/common";
import { FieldRenderConfig } from "../../types/nodes";
import InputTextBoxWithDropdown from "../Common/InputTextBoxWithDropdown";
import LabelWithHint from "../Common/LabelWithHint";

export interface LineItemEntry {
    id: string;
    key: string;
    value: string;
    valueRefNodeId?: string;
}

interface LineItemsConfigValue {
    entries: LineItemEntry[];
}

const parseValue = (value: string): LineItemsConfigValue => {
    try {
        const parsed = JSON.parse(value || '{}');
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
            return { entries: [{ id: crypto.randomUUID(), key: '', value: '' }] };
        }
        const entries = Object.entries(parsed).map(([k, v]) => ({
            id: crypto.randomUUID(),
            key: String(k || ''),
            value: String(v ?? ''),
        }));
        if (entries.length === 0) {
            entries.push({ id: crypto.randomUUID(), key: '', value: '' });
        }
        return { entries };
    } catch {
        return { entries: [{ id: crypto.randomUUID(), key: '', value: '' }] };
    }
};

type LineItemsConfigFieldProps = {
    keyName: string;
    renderConfig: FieldRenderConfig;
    displayValue: string;
    setNodeRunConfigAndRefNodeId: (key: string, value: string, refNodeId?: string) => void;
    saveConfigField: (key: string) => void;
    dropdownOptionIdToOptionMap: Record<string, ResponseConfigOption>;
    countToNameMap: Record<string, string>;
    refNodeId: string;
    responseConfig: ResponseConfigOption[];
    filterText: string;
};

const LineItemsConfigField: React.FC<LineItemsConfigFieldProps> = ({
    keyName,
    renderConfig,
    displayValue,
    setNodeRunConfigAndRefNodeId,
    saveConfigField,
    dropdownOptionIdToOptionMap,
    countToNameMap,
    refNodeId,
    responseConfig,
    filterText,
}) => {
    const [configValue, setConfigValue] = React.useState<LineItemsConfigValue>(
        () => parseValue(displayValue)
    );

    useEffect(() => {
        setConfigValue(parseValue(displayValue));
    }, [displayValue]);

    const persist = useCallback((entries: LineItemEntry[]) => {
        const mappingsObj = entries
            .filter(e => e.key?.trim())
            .reduce((acc, e) => ({ ...acc, [e.key]: e.value }), {} as Record<string, string>);
        setNodeRunConfigAndRefNodeId(keyName, JSON.stringify(mappingsObj));
        saveConfigField(keyName);
    }, [keyName, setNodeRunConfigAndRefNodeId, saveConfigField]);

    const updateMappings = useCallback((entries: LineItemEntry[]) => {
        setConfigValue({ entries });
        persist(entries);
    }, [persist]);

    const handleAddRow = useCallback(() => {
        const newEntry: LineItemEntry = { id: crypto.randomUUID(), key: '', value: '' };
        updateMappings([...configValue.entries, newEntry]);
    }, [configValue.entries, updateMappings]);

    const handleDeleteRow = useCallback((index: number) => {
        const newEntries = configValue.entries.filter((_, i) => i !== index);
        if (newEntries.length === 0) {
            newEntries.push({ id: crypto.randomUUID(), key: '', value: '' });
        }
        updateMappings(newEntries);
    }, [configValue.entries, updateMappings]);

    const handleKeyChange = useCallback((index: number, newKey: string) => {
        const newEntries = [...configValue.entries];
        newEntries[index] = { ...newEntries[index], key: newKey };
        updateMappings(newEntries);
    }, [configValue.entries, updateMappings]);

    const handleValueChange = useCallback((index: number, newValue: string, valueRefNodeId?: string) => {
        const newEntries = [...configValue.entries];
        newEntries[index] = { ...newEntries[index], value: newValue, valueRefNodeId };
        setConfigValue({ entries: newEntries });
        persist(newEntries);
    }, [configValue.entries, persist]);

    const handleValueBlur = useCallback(() => saveConfigField(keyName), [keyName, saveConfigField]);

    const keyOptions = renderConfig?.map_options?.map((opt: { key: string }) => opt.key) || [];
    const hasEmptyEntry = configValue.entries.some(e => !e.key?.trim());

    if (filterText && !(renderConfig?.label || keyName)?.toLowerCase().includes(filterText.toLowerCase())) {
        return null;
    }

    const singleContainerSx = {
        p: 2,
        borderRadius: '8px',
        bgcolor: '#F8F9FB',
        border: '2px solid #F8F9FB',
        boxSizing: 'border-box',
    };

    return (
        <Box sx={{ ...singleContainerSx, mt: 2, mb: 2 }}>
            <Stack spacing={3}>
                <Box>
                    <LabelWithHint
                        label="Line Item Mappings"
                        hint="Add key-value pairs to map Oracle fields to source columns"
                        required={false}
                    />
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {configValue.entries.map((entry, index) => (
                            <Stack
                                key={entry.id}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                            <Box sx={{ flex: '0 0 35%', minWidth: 0 }}>
                                {keyOptions.length > 0 ? (
                                    <Autocomplete
                                        freeSolo
                                        size="small"
                                        options={keyOptions}
                                        inputValue={entry.key}
                                        onInputChange={(_, newValue) => handleKeyChange(index, newValue)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Field name or type freely"
                                                sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                            />
                                        )}
                                    />
                                ) : (
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Field name"
                                        value={entry.key}
                                        onChange={(e) => handleKeyChange(index, e.target.value)}
                                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                    />
                                )}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <InputTextBoxWithDropdown
                                    key={`value-${index}-${entry.valueRefNodeId || 'static'}`}
                                    editableInput={entry.value}
                                    setEditableValue={(textValue, valueRefNodeId) => handleValueChange(index, textValue, valueRefNodeId)}
                                    refNodeId={entry.valueRefNodeId || refNodeId}
                                    dropdownOptions={responseConfig}
                                    cssOverrides="w-full"
                                    saveOnBlur={handleValueBlur}
                                    dropdownOptionIdToOptionMap={dropdownOptionIdToOptionMap}
                                    countToNameMap={countToNameMap}
                                />
                            </Box>
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteRow(index)}
                                sx={{ flexShrink: 0 }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    ))}
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        className="!bg-nanonetsBlue !normal-case !text-white"
                        sx={{ alignSelf: 'flex-start' }}
                        onClick={() => {
                            if (hasEmptyEntry) return;
                            handleAddRow();
                        }}
                    >
                        Add Mappings
                    </Button>
                </Stack>
                </Box>
            </Stack>
        </Box>
    );
};

export default LineItemsConfigField;
